import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { FaUsers, FaBox, FaCar, FaFlag, FaTrash, FaArrowLeft, FaUserSlash, FaUserCheck } from 'react-icons/fa';
import PublicProfile from './PublicProfile';
import AdminChatViewer from './AdminChatViewer';

const API = 'https://sendvigo-backend-production.up.railway.app';

export default function AdminPanel({ onBack }) {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [reportes, setReportes] = useState([]);
  const [tab, setTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  const [viendoPerfilId, setViendoPerfilId] = useState(null);
  const [viendoChatPackageId, setViendoChatPackageId] = useState(null);
  const [respuestas, setRespuestas] = useState({}); // { [reportId]: texto en edición }
  const [mensaje, setMensaje] = useState('');

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API}/admin/stats`, { headers })
      .then(r => r.json()).then(setStats).catch(() => {});
    setLoading(false);
  }, []);

  const cargarUsuarios = async () => {
    const r = await fetch(`${API}/admin/usuarios`, { headers });
    const data = await r.json();
    setUsuarios(data);
  };

  const cargarReportes = async () => {
    const r = await fetch(`${API}/admin/reportes`, { headers });
    const data = await r.json();
    setReportes(data);
  };

  useEffect(() => {
    if (tab === 'usuarios') cargarUsuarios();
    if (tab === 'reportes') cargarReportes();
  }, [tab]);

  const eliminarUsuario = async (id) => {
    if (!window.confirm('¿Eliminar este usuario? Esta acción es irreversible.')) return;
    await fetch(`${API}/admin/usuarios/${id}`, { method: 'DELETE', headers });
    cargarUsuarios();
  };

  const toggleSuspension = async (userId, suspenderAhora) => {
    const accion = suspenderAhora ? 'suspender' : 'reactivar';
    if (!window.confirm(`¿Seguro que quieres ${accion} a este usuario?`)) return;
    try {
      await fetch(`${API}/admin/usuarios/${userId}/suspender`, {
        method: 'PUT', headers,
        body: JSON.stringify({ suspendido: suspenderAhora })
      });
      setMensaje(suspenderAhora ? 'Usuario suspendido' : 'Usuario reactivado');
      setTimeout(() => setMensaje(''), 3000);
      cargarReportes();
    } catch {
      setMensaje('Error al actualizar el usuario');
    }
  };

  const cambiarEstado = async (reportId, estado) => {
    try {
      await fetch(`${API}/admin/reportes/${reportId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ estado })
      });
      cargarReportes();
    } catch {
      setMensaje('Error al actualizar el reporte');
    }
  };

  const enviarRespuesta = async (reportId) => {
    const texto = respuestas[reportId]?.trim();
    if (!texto) return;
    try {
      await fetch(`${API}/admin/reportes/${reportId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ respuestaAdmin: texto, estado: 'resuelto' })
      });
      setRespuestas(prev => ({ ...prev, [reportId]: '' }));
      setMensaje('Respuesta enviada');
      setTimeout(() => setMensaje(''), 3000);
      cargarReportes();
    } catch {
      setMensaje('Error al enviar la respuesta');
    }
  };

  if (viendoPerfilId) {
    return <PublicProfile userId={viendoPerfilId} onBack={() => setViendoPerfilId(null)} />;
  }

  if (viendoChatPackageId) {
    return <AdminChatViewer packageId={viendoChatPackageId} onBack={() => setViendoChatPackageId(null)} />;
  }

  return (
    <div style={{minHeight:'100vh', backgroundColor:'#111318', paddingBottom:'40px'}}>

      {/* Header */}
      <div style={{backgroundColor:'#1a1d24', borderBottom:'1px solid #2a2d35', padding:'16px'}}>
        <div style={{display:'flex', alignItems:'center', gap:'12px', maxWidth:'480px', margin:'0 auto'}}>
          <button onClick={onBack}
            style={{backgroundColor:'#2a2d35', color:'#9ca3af', padding:'8px', borderRadius:'10px', border:'none', cursor:'pointer'}}>
            <FaArrowLeft />
          </button>
          <div>
            <p style={{color:'white', fontWeight:'800', fontSize:'16px'}}>Panel de Admin</p>
            <p style={{color:'#f97316', fontSize:'11px'}}>SenDViGo</p>
          </div>
        </div>
      </div>

      <div style={{maxWidth:'480px', margin:'0 auto', padding:'16px'}}>

        {mensaje && (
          <div style={{backgroundColor:'#22c55e15', border:'1px solid #22c55e30', color:'#4ade80',
            borderRadius:'12px', padding:'10px 14px', fontSize:'13px', marginBottom:'16px', textAlign:'center'}}>
            {mensaje}
          </div>
        )}

        {/* Tabs */}
        <div style={{display:'flex', gap:'8px', marginBottom:'20px'}}>
          {[
            { key: 'stats', label: '📊 Stats' },
            { key: 'usuarios', label: '👥 Usuarios' },
            { key: 'reportes', label: '🚨 Reportes' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{flex:1, padding:'10px', borderRadius:'12px', fontSize:'12px', fontWeight:'600',
                backgroundColor: tab === t.key ? '#f97316' : '#1a1d24',
                color: tab === t.key ? 'white' : '#6b7280',
                border: `1px solid ${tab === t.key ? '#f97316' : '#2a2d35'}`,
                cursor:'pointer'}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* STATS */}
        {tab === 'stats' && stats && (
          <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
              {[
                { icon: <FaUsers />, label: 'Usuarios', value: stats.totalUsuarios, color: '#3b82f6' },
                { icon: <FaUsers />, label: 'Nuevos hoy', value: stats.usuariosHoy, color: '#22c55e' },
                { icon: <FaBox />, label: 'Envíos', value: stats.totalEnvios, color: '#f97316' },
                { icon: <FaBox />, label: 'Completados', value: stats.enviosCompletados, color: '#22c55e' },
                { icon: <FaCar />, label: 'Viajes', value: stats.totalViajes, color: '#a855f7' },
                { icon: <FaFlag />, label: 'Reportes', value: stats.totalReportes, color: '#f87171' },
              ].map(s => (
                <div key={s.label} style={{backgroundColor:'#1a1d24', border:'1px solid #2a2d35',
                  borderRadius:'14px', padding:'16px', textAlign:'center'}}>
                  <div style={{color: s.color, fontSize:'20px', marginBottom:'6px'}}>{s.icon}</div>
                  <p style={{color:'white', fontWeight:'800', fontSize:'24px'}}>{s.value}</p>
                  <p style={{color:'#6b7280', fontSize:'11px'}}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* USUARIOS */}
        {tab === 'usuarios' && (
          <div>
            <p style={{color:'#6b7280', fontSize:'12px', marginBottom:'12px'}}>{usuarios.length} usuarios registrados</p>
            {usuarios.map(u => (
              <div key={u.id} style={{backgroundColor:'#1a1d24', border:'1px solid #2a2d35',
                borderRadius:'14px', padding:'14px', marginBottom:'8px',
                display:'flex', alignItems:'center', gap:'12px'}}>
                {u.avatar ? (
                  <img src={u.avatar} style={{width:'40px', height:'40px', borderRadius:'50%', objectFit:'cover', flexShrink:0}} />
                ) : (
                  <div style={{width:'40px', height:'40px', borderRadius:'50%', backgroundColor:'#f97316',
                    display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:'bold', flexShrink:0}}>
                    {u.name?.charAt(0)}
                  </div>
                )}
                <div style={{flex:1, minWidth:0, cursor:'pointer'}} onClick={() => setViendoPerfilId(u.id)}>
                  <p style={{color:'white', fontSize:'13px', fontWeight:'600'}}>{u.name}</p>
                  <p style={{color:'#6b7280', fontSize:'11px'}}>{u.email}</p>
                  <div style={{display:'flex', gap:'6px', marginTop:'3px'}}>
                    <span style={{backgroundColor: u.role === 'admin' ? '#f9731620' : '#2a2d35',
                      color: u.role === 'admin' ? '#f97316' : '#6b7280',
                      fontSize:'10px', padding:'2px 8px', borderRadius:'10px'}}>
                      {u.role}
                    </span>
                    <span style={{color:'#3a3d45', fontSize:'10px'}}>
                      {new Date(u.createdAt).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                </div>
                {u.role !== 'admin' && (
                  <button onClick={() => eliminarUsuario(u.id)}
                    style={{backgroundColor:'#f8717115', border:'1px solid #f8717130', color:'#f87171',
                      padding:'8px', borderRadius:'10px', cursor:'pointer'}}>
                    <FaTrash style={{fontSize:'12px'}} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* REPORTES */}
        {tab === 'reportes' && (
          <div>
            <p style={{color:'#6b7280', fontSize:'12px', marginBottom:'12px'}}>{reportes.length} reportes recibidos</p>
            {reportes.length === 0 && (
              <div style={{textAlign:'center', padding:'40px 0'}}>
                <p style={{fontSize:'32px', opacity:0.2, marginBottom:'8px'}}>🚨</p>
                <p style={{color:'#6b7280', fontSize:'13px'}}>No hay reportes</p>
              </div>
            )}
            {reportes.map(r => (
              <div key={r.id} style={{backgroundColor:'#1a1d24', border:'1px solid #2a2d35',
                borderRadius:'14px', padding:'14px', marginBottom:'10px'}}>

                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <span style={{backgroundColor:'#f8717115', border:'1px solid #f8717130',
                      color:'#f87171', fontSize:'11px', fontWeight:'600', padding:'3px 10px', borderRadius:'20px'}}>
                      {r.categoria}
                    </span>
                    <span style={{
                      backgroundColor: r.estado === 'resuelto' ? '#22c55e15' : '#f59e0b15',
                      border: `1px solid ${r.estado === 'resuelto' ? '#22c55e30' : '#f59e0b30'}`,
                      color: r.estado === 'resuelto' ? '#4ade80' : '#f59e0b',
                      fontSize:'10px', fontWeight:'600', padding:'3px 8px', borderRadius:'20px'}}>
                      {r.estado === 'resuelto' ? '✓ Resuelto' : '⏳ Pendiente'}
                    </span>
                  </div>
                  <span style={{color:'#3a3d45', fontSize:'11px'}}>
                    {new Date(r.createdAt).toLocaleDateString('es-ES')}
                  </span>
                </div>

                {/* Personas implicadas — clicables */}
                <div style={{display:'flex', gap:'8px', marginBottom:'8px', flexWrap:'wrap'}}>
                  <button onClick={() => setViendoPerfilId(r.fromUser?.id)}
                    style={{backgroundColor:'transparent', border:'1px solid #2a2d35', borderRadius:'10px',
                      padding:'4px 10px', fontSize:'11px', color:'#9ca3af', cursor:'pointer'}}>
                    De: <span style={{color:'white', fontWeight:'600'}}>{r.fromUser?.name}</span>
                  </button>
                  <button onClick={() => setViendoPerfilId(r.toUser?.id)}
                    style={{backgroundColor:'transparent', border:'1px solid #2a2d35', borderRadius:'10px',
                      padding:'4px 10px', fontSize:'11px', color:'#9ca3af', cursor:'pointer'}}>
                    A: <span style={{color:'#f87171', fontWeight:'600'}}>{r.toUser?.name}</span>
                  </button>
                </div>

                <p style={{color:'#6b7280', fontSize:'12px', marginBottom:'4px'}}>{r.subcategoria}</p>
                {r.detalle && <p style={{color:'#9ca3af', fontSize:'12px', marginBottom:'10px', fontStyle:'italic'}}>"{r.detalle}"</p>}

                {r.capturaUrl && (
                  <a href={r.capturaUrl} target="_blank" rel="noopener noreferrer" style={{display:'block', marginBottom:'10px'}}>
                    <img src={r.capturaUrl} alt="Captura adjunta" style={{
                      width:'100%', maxHeight:'160px', objectFit:'cover', borderRadius:'10px',
                      border:'1px solid #2a2d35',
                    }} />
                  </a>
                )}

                {r.package && (
                  <button onClick={() => setViendoChatPackageId(r.package.id)}
                    style={{width:'100%', backgroundColor:'#3b82f615', border:'1px solid #3b82f630',
                      color:'#3b82f6', fontSize:'12px', fontWeight:'600', padding:'9px', borderRadius:'10px',
                      cursor:'pointer', marginBottom:'10px'}}>
                    📦 Ver chat del envío: {r.package.from} → {r.package.to}
                  </button>
                )}

                {r.respuestaAdmin && (
                  <div style={{backgroundColor:'#f9731610', border:'1px solid #f9731625', borderRadius:'10px',
                    padding:'10px', marginBottom:'10px'}}>
                    <p style={{color:'#f97316', fontSize:'10px', fontWeight:'600', marginBottom:'3px'}}>Tu respuesta:</p>
                    <p style={{color:'#9ca3af', fontSize:'12px'}}>{r.respuestaAdmin}</p>
                  </div>
                )}

                {/* Responder */}
                <textarea
                  value={respuestas[r.id] || ''}
                  onChange={e => setRespuestas(prev => ({ ...prev, [r.id]: e.target.value }))}
                  placeholder="Escribe una respuesta para quien reportó..."
                  rows={2}
                  style={{width:'100%', backgroundColor:'#111318', border:'1px solid #2a2d35', color:'white',
                    borderRadius:'10px', padding:'8px 10px', fontSize:'12px', resize:'none', marginBottom:'8px'}}
                />

                <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
                  <button onClick={() => enviarRespuesta(r.id)}
                    style={{flex:1, backgroundColor:'#f97316', color:'white', fontSize:'11px', fontWeight:'600',
                      padding:'8px', borderRadius:'10px', border:'none', cursor:'pointer', minWidth:'100px'}}>
                    Enviar respuesta
                  </button>
                  <button onClick={() => cambiarEstado(r.id, r.estado === 'resuelto' ? 'pendiente' : 'resuelto')}
                    style={{backgroundColor:'#2a2d35', color:'#9ca3af', fontSize:'11px', fontWeight:'600',
                      padding:'8px 12px', borderRadius:'10px', border:'none', cursor:'pointer'}}>
                    {r.estado === 'resuelto' ? 'Reabrir' : 'Marcar resuelto'}
                  </button>
                  {r.toUser?.suspendido ? (
                    <button onClick={() => toggleSuspension(r.toUser.id, false)}
                      style={{backgroundColor:'#22c55e15', border:'1px solid #22c55e30', color:'#4ade80',
                        fontSize:'11px', fontWeight:'600', padding:'8px 12px', borderRadius:'10px', cursor:'pointer',
                        display:'flex', alignItems:'center', gap:'5px'}}>
                      <FaUserCheck style={{fontSize:'11px'}} /> Reactivar
                    </button>
                  ) : (
                    <button onClick={() => toggleSuspension(r.toUser?.id, true)}
                      style={{backgroundColor:'#f8717115', border:'1px solid #f8717130', color:'#f87171',
                        fontSize:'11px', fontWeight:'600', padding:'8px 12px', borderRadius:'10px', cursor:'pointer',
                        display:'flex', alignItems:'center', gap:'5px'}}>
                      <FaUserSlash style={{fontSize:'11px'}} /> Suspender
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}